import type { User, UserManager } from 'oidc-client-ts'
import { setAuthTokenToCookies } from 'api/auth'

// Renew this far ahead of expiry. Assumes the access-token TTL comfortably exceeds
// it: a TTL at or below this would read as permanently stale and renew every time.
export const OIDC_REFRESH_LEEWAY_SECONDS = 600

let userManagerPromise: Promise<UserManager> | undefined
let renewInFlight: Promise<string | undefined> | undefined

// One long-lived UserManager shared with the coordinator, so silent renew stays
// armed and two managers don't race the rotating refresh token. oidc-client-ts
// stays lazy (./config) for bundle isolation from non-OIDC brands.
export function getOidcUserManager(): Promise<UserManager> {
  if (!userManagerPromise) {
    userManagerPromise = createOidcUserManagerAsync()
  }
  return userManagerPromise
}

async function createOidcUserManagerAsync(): Promise<UserManager> {
  try {
    const { createOidcUserManager } = await import('./config')
    return createOidcUserManager()
  } catch(error) {
    // Don't memoise a failed construction, or 401 recovery stays dead until reload.
    userManagerPromise = undefined
    throw error
  }
}

export function isAccessTokenStale(user: User | null | undefined, nowSeconds: number): boolean {
  if (!user?.access_token || typeof user.expires_at !== 'number') {
    return true
  }
  return nowSeconds >= user.expires_at - OIDC_REFRESH_LEEWAY_SECONDS
}

async function renew(): Promise<string | undefined> {
  const userManager = await getOidcUserManager()
  const refreshed = await userManager.signinSilent()
  const accessToken = refreshed?.access_token
  if (accessToken) {
    setAuthTokenToCookies(accessToken)
  }
  return accessToken
}

// Single-flight around the renew alone, so the rotating refresh token is spent
// once. The freshness check stays outside it: sharing that too would let a forced
// caller receive the very token the server just rejected.
function renewOnce(): Promise<string | undefined> {
  if (!renewInFlight) {
    renewInFlight = renew().finally(() => {
      renewInFlight = undefined
    })
  }
  return renewInFlight
}

// force: the caller knows the current token is unacceptable (a 401), so skip the
// freshness check and always renew.
export async function ensureValidOidcSession(options?: { force?: boolean }): Promise<string | undefined> {
  if (!options?.force) {
    const userManager = await getOidcUserManager()
    const current = await userManager.getUser()
    if (current?.access_token && !isAccessTokenStale(current, Date.now() / 1000)) {
      setAuthTokenToCookies(current.access_token)
      return current.access_token
    }
  }

  return renewOnce()
}
